import { useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaTint } from 'react-icons/fa';
import type { HourlyForecast, MichiganTimezone } from '@miweather/shared';
import { WeatherIcon } from './WeatherIcon';
import { getTimezoneAbbrev } from '@/lib/weather';

export function HourlyForecastSection({ hourly, locationName, timezone }: { hourly: HourlyForecast[]; locationName: string; timezone: MichiganTimezone }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tzAbbrev = getTimezoneAbbrev(timezone);

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  return (
    <div>
      <h2 className="text-base font-bold mb-3">Hourly Forecast for {locationName} <span className="text-sm font-normal text-white/60">({tzAbbrev})</span></h2>

      <div className="relative rounded-xl stat-box overflow-hidden">
        <div ref={scrollRef} className="flex overflow-x-auto scrollbar-hide px-10">
          {hourly.slice(0, 24).map((hour, i) => (
            <HourlyCard key={i} hour={hour} timezone={timezone} />
          ))}
        </div>

        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center text-white/50 hover:text-white transition-colors bg-gradient-to-r from-black/40 to-transparent"
          aria-label="Scroll hourly forecast left"
        >
          <FaChevronLeft className="text-sm" aria-hidden="true" />
        </button>

        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 w-10 flex items-center justify-center text-white/50 hover:text-white transition-colors bg-gradient-to-l from-black/40 to-transparent"
          aria-label="Scroll hourly forecast right"
        >
          <FaChevronRight className="text-sm" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function HourlyCard({ hour, timezone }: { hour: HourlyForecast; timezone: MichiganTimezone }) {
  const time = new Date(hour.time).toLocaleTimeString('en-US', { hour: 'numeric', timeZone: timezone });

  return (
    <div className="flex-shrink-0 flex flex-col items-center py-4 px-4 min-w-[72px]">
      <p className="text-sm text-white/60 mb-2">{time}</p>
      <WeatherIcon icon={hour.icon} size={28} className="text-white/90 mb-2" />
      <p className="text-xs text-[#4ba3c3] flex items-center gap-0.5 mb-2">
        <FaTint className="text-[10px]" />
        {hour.precipProbability}%
      </p>
      <p className="text-lg font-bold">{hour.temperature}°</p>
    </div>
  );
}
